import {
  AppBar as AlchemyAppBar,
  AppLogo as AlchemyAppLogo,
  AppMenuButtonMobile as AlchemyAppMenuButtonMobile,
  AppNav as AlchemyAppNav,
  AppNavListItem as AlchemyAppNavListItem,
  IconButton as AlchemyIconButton,
  MenuButton as AlchemyMenuButton,
  OverflowCollapse as AlchemyOverflowCollapse,
  UserIcon as AlchemyUserIcon,
  atMinWidth,
  borderRadius,
  Button,
  color,
  snap,
  typeStyle,
  units
} from '@m/alchemy-ui';
import styled, { css } from 'styled-components';
import { Breakpoints } from '../../common/enums';

export const ACTIVE_OVERFLOW_LINK_CLASSNAME = 'active-overflow-link-classname';

export const AppNavSeparator = styled.span`
  background: ${color('line')};
  height: ${snap(28)};
  margin: 0 ${units(2)};
  width: 1px;
`;

export const NavProfileImage = styled.img`
  border-radius: 50%;
  height: ${snap(30)};
  margin-right: ${units(1)};
  width: ${snap(30)};
`;

export const UserIcon = styled(AlchemyUserIcon)`
  /* Alchemy is overzealous with targeting SVG styles when nested in a AppNavListItem */
  height: ${snap(30)} !important;
  margin-right: ${units(1)} !important;
  width: ${snap(30)} !important;
`;

/* z-index: 998 because we need to have a smaller number than
ul.b-header__main-menu li (desktop/shared.css ) but enough to
avoid other position conflicts */
export const AppBarWrapper = styled.header<{ isSticky: boolean }>`
  border-bottom: 1px solid ${color('line')};
  left: 0;
  top: 0;
  width: 100%;
  z-index: 998;

  ${(props) =>
    props.isSticky
      ? css`
          position: fixed;
        `
      : css`
          position: static;
        `}

  ${atMinWidth(
    Breakpoints.DESKTOP_MIN,
    `
      body.mdl-wrapper__body.moderator & {
        /* Height of community admin bar */
        top: 65px;
      }
    `
  )};
`;

export const AppLogo = styled(AlchemyAppLogo)`
  display: block;
  margin: 0 ${units(2)};

  ${atMinWidth(
    Breakpoints.TABLET_MIN,
    `
      margin: 0 ${units(2)} 0 0;
    `
  )};
`;

interface AppNavButtonProps {
  readonly alignLeft?: boolean;
  readonly hide?: boolean;
}

export const AppNavButton = styled(Button)<AppNavButtonProps>`
  &:hover {
    text-decoration: none;
  }

  svg {
    margin-right: ${units(1)};
  }

  ${({ alignLeft }) =>
    alignLeft
      ? css`
          margin-left: ${units(1)};
          margin-right: auto;
        `
      : ''}

  ${({ hide }) =>
    hide &&
    css`
      display: none;
    `}
`;

export const IconButton = styled(AlchemyIconButton)<{ hide?: boolean }>`
  margin-left: ${units(1.5)};

  svg,
  svg:hover {
    color: ${color('base', { palette: 'interactiveConstructive' })} !important;
  }

  ${({ hide }) =>
    hide &&
    css`
      display: none;
    `}
`;

export const CommunityLogo = styled.img`
  max-height: ${snap(38)};
  max-width: ${snap(220)};
  border-radius: ${borderRadius()};
`;

export const OverflowCollapse = styled(AlchemyOverflowCollapse)`
  align-items: center;
`;

export const MenuButton = styled(AlchemyMenuButton)`
  margin-left: ${units(1)};
  margin-right: auto;
`;

export const AppNav = styled(AlchemyAppNav)`
  margin-right: 0 !important;

  ${atMinWidth(
    Breakpoints.MONITOR_MIN,
    `
      margin-right: ${units(2)};
    `
  )};
`;

export const AppBar = styled(AlchemyAppBar)`
  > * {
    height: auto !important;
    flex-direction: column;

    ${atMinWidth(
      Breakpoints.MONITOR_MIN,
      `
        flex-direction: row;
      `
    )};
  }
`;

export const AppPrimaryBar = styled.div`
  align-items: center;
  display: flex;
  flex-grow: 1;
  height: 52px;
`;

export const AppMenuButtonMobile = styled(AlchemyAppMenuButtonMobile)`
  line-height: 1;
`;

export const AppSecondaryBar = styled.div`
  align-items: center;
  border-top: 1px solid ${color('line')};
  display: flex;
  flex-shrink: 0;
  height: 52px;
  margin: 0 -${units(2)};
  padding: 0 ${units(2)};

  ${atMinWidth(
    Breakpoints.MONITOR_MIN,
    `
      border-top: none;
    `
  )};

  ${atMinWidth(
    Breakpoints.MONITOR_MIN,
    `
        border-top: none;
      `
  )};
`;

export const AppMobileButtonLabel = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;

  span {
    ${typeStyle('bodyM')};
  }
`;

export const AppNavListItem = styled(AlchemyAppNavListItem)`
  a:hover {
    text-decoration: none;
    box-shadow: inset 0px -6px 0px ${color('hint', { palette: 'interactiveConstructive' })};
  }

  a.${ACTIVE_OVERFLOW_LINK_CLASSNAME} {
    box-shadow: inset 0px -6px 0px ${color('base', { palette: 'interactiveConstructive' })};
  }
`;

export const SearchButtonWrapper = styled.div`
  height: 100%;
  display: flex;
  align-items: center;
  margin-right: ${units(1.5)};
`;

export const ButtonWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-grow: 1;
  align-items: center;
  justify-content: flex-end;
`;

export const SocialButtonWrapper = styled.div`
  padding: 0 5px;

  ${atMinWidth(
    Breakpoints.TABLET_MIN,
    `
      padding: 0 ${units(1)};
    `
  )};
`;
